import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import {
  AttachMoney,
  Description,
  People,
  TrendingUp,
  Work,
  Business,
  BarChart,
  Settings,
  CalendarToday,
  AccountBalance,
  Assessment,
  ShowChart,
  Person,
  AccountTree,
  EventNote,
  SwapHoriz,
  AccountBalanceWallet,
  Receipt,
  SwapCalls,
  VpnKey,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Logout
} from '@mui/icons-material';
import treasuryLogo from '../assets/treasury-logo.png';
import NotificationBell from './NotificationBell';
import './SideNav.css';

const DRAWER_WIDTH_EXPANDED = 220;
const DRAWER_WIDTH_COLLAPSED = 64;

const ICON_MAP = {
  payment_master: AttachMoney,
  isin_master: Description,
  counterparty_master: People,
  limit_setup: TrendingUp,
  portfolio_master: Work,
  broker_master: Business,
  strategy_master: BarChart,
  settlement_account_master: Settings,
  holiday_calendar: CalendarToday,
  fund_centre_master: AccountBalance,
  investment_approver_master: People,
  issuer_master: AccountBalance,
  gsec_report: Assessment,
  tbill_report: Assessment,
  gsec_summary_report: Assessment,
  'money-market-report': BarChart,
  'mark-to-market-report': ShowChart,
  'repo-report': Assessment,
  'buyback-report': Assessment,
  'sell-transaction-report': Assessment,
  'portfolio-report': Assessment,
  'counterparty-report': Assessment,
  'coupon-maturity-blotter': EventNote,
  'broker-report': Business,
  'daily-cashflow-blotter': AccountBalanceWallet,
  transactions: Receipt,
  fixed_income_gsec: AccountBalanceWallet,
  fixed_income_tbill: AccountBalanceWallet,
  fixed_income_others: AccountBalanceWallet,
  money_market: AttachMoney,
  buyback: SwapHoriz,
  maturity: EventNote,
  'daily-deal-blotter': Receipt,
  'daily-transaction-blotter': Receipt,
  repo: SwapCalls,
  reverse_repo: ShowChart,
  payments_rtgs: AttachMoney,
  payments_other: AttachMoney,
  admin_users: Person,
  admin_accounting: AccountTree,
  eod: EventNote
};

const GROUP_ICONS = {
  masters: Settings,
  reports: Assessment,
  operations: Receipt,
  admin: Person
};

const getIcon = (key) => {
  const IconComponent = ICON_MAP[key] || Description;
  return <IconComponent />;
};

function SideNav({ allowedTabs }) {
  const location = useLocation();
  const flyoutCloseTimer = useRef(null);
  const [flyoutGroup, setFlyoutGroup] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(72);

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true;
  });

  const [openGroups, setOpenGroups] = useState(() => {
    const saved = localStorage.getItem('sidebarGroupsState');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { masters: false, reports: false, operations: false, admin: false };
      }
    }
    return { masters: false, reports: false, operations: false, admin: false };
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  // Middle office is an independent assignment axis (see ProtectedRoute's
  // assignmentRolePrefix) - check membership directly, not user.role, so a
  // user holding both a deal-workflow role and a middle-office assignment
  // still sees Middle Office links.
  const isMiddleOffice = isAdmin || (Array.isArray(user.assignments) && user.assignments.some(
    (a) => typeof a.role === 'string' && a.role.startsWith('middle_office_')
  ));

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    setFlyoutGroup(null);
    localStorage.setItem('sidebarCollapsed', next.toString());
  };

  const persistGroups = (next) => {
    setOpenGroups(next);
    localStorage.setItem('sidebarGroupsState', JSON.stringify(next));
  };

  const toggleGroup = (groupKey) => {
    persistGroups({ ...openGroups, [groupKey]: !openGroups[groupKey] });
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      collapsed ? `${DRAWER_WIDTH_COLLAPSED}px` : `${DRAWER_WIDTH_EXPANDED}px`
    );
  }, [collapsed]);

  const groupedTabs = useMemo(() => {
    const mastersKeys = new Set([
      'payment_master', 'isin_master', 'counterparty_master', 'limit_setup',
      'portfolio_master', 'broker_master', 'strategy_master', 'settlement_account_master', 'holiday_calendar', 'fund_centre_master',
      'investment_approver_master', 'issuer_master'
    ]);
    const reportKeys = new Set([
      'gsec_report', 'tbill_report', 'gsec_summary_report', 'money-market-report', 'mark-to-market-report', 'repo-report', 'buyback-report', 'sell-transaction-report', 'portfolio-report', 'counterparty-report', 'coupon-maturity-blotter', 'broker-report'
    ]);
    const adminKeys = new Set(['admin_users', 'admin_accounting', 'admin_settlement_preview', 'eod']);
    const markToMarketKey = 'reverse_repo';

    const groups = { masters: [], reports: [], operations: [], admin: [], markToMarket: [] };
    for (const tab of allowedTabs) {
      if (tab.key === markToMarketKey) {
        groups.markToMarket.push(tab);
      } else if (adminKeys.has(tab.key)) {
        groups.admin.push(tab);
      } else if (mastersKeys.has(tab.key)) {
        groups.masters.push(tab);
      } else if (reportKeys.has(tab.key)) {
        groups.reports.push(tab);
      } else {
        groups.operations.push(tab);
      }
    }
    return groups;
  }, [allowedTabs]);

  useEffect(() => {
    const path = location.pathname;
    const groupKey = ['masters', 'reports', 'operations', 'admin'].find((key) =>
      (groupedTabs[key] || []).some((tab) => tab.path === path)
    );
    if (!groupKey) return;
    setOpenGroups((prev) => {
      if (prev[groupKey]) return prev;
      const next = { ...prev, [groupKey]: true };
      localStorage.setItem('sidebarGroupsState', JSON.stringify(next));
      return next;
    });
  }, [location.pathname, groupedTabs]);

  const openFlyout = (groupKey, event) => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = 56 + ((groupedTabs[groupKey] || []).length * 42);
    const top = Math.min(Math.max(12, rect.top), window.innerHeight - estimatedHeight - 12);
    setFlyoutTop(top);
    setFlyoutGroup(groupKey);
  };

  const scheduleCloseFlyout = () => {
    if (flyoutCloseTimer.current) clearTimeout(flyoutCloseTimer.current);
    flyoutCloseTimer.current = setTimeout(() => setFlyoutGroup(null), 160);
  };

  useEffect(() => () => {
    if (flyoutCloseTimer.current) clearTimeout(flyoutCloseTimer.current);
  }, []);

  const NavLink = ({ to, icon, label, nested = false, onNavigate }) => {
    const selected = location.pathname === to;
    const className = [
      'sn-item',
      nested ? 'sn-item--nested' : '',
      selected ? 'is-active' : ''
    ].filter(Boolean).join(' ');

    return (
      <Link to={to} className={className} onClick={onNavigate}>
        <span className="sn-item-icon">{icon}</span>
        <span className="sn-item-label">{label}</span>
      </Link>
    );
  };

  const Group = ({ title, groupKey, items }) => {
    if (!items.length) return null;
    const GroupIcon = GROUP_ICONS[groupKey] || Description;
    const hasActive = items.some((tab) => tab.path === location.pathname);

    if (collapsed) {
      return (
        <div
          className="sn-rail-group"
          onMouseOver={(event) => openFlyout(groupKey, event)}
          onMouseLeave={scheduleCloseFlyout}
        >
          <Tooltip title={title} placement="right">
            <button
              type="button"
              className={`sn-item sn-item--collapsed${hasActive ? ' is-active' : ''}`}
              aria-label={title}
              aria-haspopup="true"
              aria-expanded={flyoutGroup === groupKey}
            >
              <span className="sn-item-icon"><GroupIcon /></span>
            </button>
          </Tooltip>
        </div>
      );
    }

    const open = Boolean(openGroups[groupKey]);
    return (
      <div className={`sn-category${open ? ' is-expanded' : ''}`}>
        <button
          type="button"
          className="sn-category-toggle"
          onClick={() => toggleGroup(groupKey)}
          aria-expanded={open}
        >
          <span className="sn-category-title">{title}</span>
          <ExpandMore className={`sn-category-chevron${open ? ' is-open' : ''}`} />
        </button>
        {open && (
          <div className="sn-menu">
            {items.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                icon={getIcon(tab.key)}
                label={tab.label}
                nested
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const flyoutItems = flyoutGroup ? groupedTabs[flyoutGroup] || [] : [];
  const flyoutTitle = {
    masters: 'Masters',
    reports: 'Reports',
    operations: 'Operations',
    admin: 'Admin'
  }[flyoutGroup] || '';

  return (
    <Drawer
      variant="permanent"
      className="sn-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
          boxSizing: 'border-box',
          overflowX: 'visible',
          overflowY: 'hidden',
          transition: 'width 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - var(--status-bar-height, 36px))'
        }
      }}
    >
      <div className={`sn-header${collapsed ? ' sn-header--collapsed' : ' sn-header--expanded'}`}>
        {!collapsed && (
          <img src={treasuryLogo} alt="Sherwood Treasury" className="sn-logo" />
        )}
        <IconButton onClick={toggleSidebar} size="small" className="sn-toggle" aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}>
          {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
        </IconButton>
      </div>

      <div className="sn-scroll">
        <Group title="Masters" groupKey="masters" items={groupedTabs.masters} />
        <Group title="Reports" groupKey="reports" items={groupedTabs.reports} />
        <Group title="Operations" groupKey="operations" items={groupedTabs.operations} />
        {groupedTabs.markToMarket.length > 0 && (
          collapsed ? (
            <Tooltip title={groupedTabs.markToMarket[0].label} placement="right">
              <Link
                to={groupedTabs.markToMarket[0].path}
                className={`sn-item sn-item--collapsed${location.pathname === groupedTabs.markToMarket[0].path ? ' is-active' : ''}`}
              >
                <span className="sn-item-icon">{getIcon('mark-to-market-report')}</span>
              </Link>
            </Tooltip>
          ) : (
            <div className="sn-category">
              <div className="sn-menu">
                <NavLink
                  to={groupedTabs.markToMarket[0].path}
                  icon={getIcon('mark-to-market-report')}
                  label={groupedTabs.markToMarket[0].label}
                />
              </div>
            </div>
          )
        )}
        {/* Non-admins see this group only for the accounting tabs an admin granted them. */}
        {(isAdmin || groupedTabs.admin.length > 0) && (
          <Group title={isAdmin ? 'Admin' : 'Accounting'} groupKey="admin" items={groupedTabs.admin} />
        )}
        {isAdmin && (
          collapsed ? (
            <>
              <Tooltip title="Authorizers" placement="right">
                <Link
                  to="/admin/authorizers"
                  className={`sn-item sn-item--collapsed${location.pathname === '/admin/authorizers' ? ' is-active' : ''}`}
                >
                  <span className="sn-item-icon"><Person /></span>
                </Link>
              </Tooltip>
              <Tooltip title="Middle Office Access" placement="right">
                <Link
                  to="/admin/middle-office-access"
                  className={`sn-item sn-item--collapsed${location.pathname === '/admin/middle-office-access' ? ' is-active' : ''}`}
                >
                  <span className="sn-item-icon"><People /></span>
                </Link>
              </Tooltip>
            </>
          ) : (
            <div className="sn-category">
              <div className="sn-menu">
                <NavLink to="/admin/authorizers" icon={<Person />} label="Authorizers" />
                <NavLink to="/admin/middle-office-access" icon={<People />} label="Middle Office Access" />
              </div>
            </div>
          )
        )}
        {/* Dealer Limits: visible to admins AND non-admin middle-office users
            (unlike Authorizers/Middle Office Access above, which stay
            admin-only - assigning access is an admin action, but managing
            dealer limits is the middle office's own day-to-day setup task). */}
        {isMiddleOffice && (
          collapsed ? (
            <>
              <Tooltip title="Dealer Limits" placement="right">
                <Link
                  to="/admin/dealer-limits"
                  className={`sn-item sn-item--collapsed${location.pathname === '/admin/dealer-limits' ? ' is-active' : ''}`}
                >
                  <span className="sn-item-icon"><AttachMoney /></span>
                </Link>
              </Tooltip>
              <Tooltip title="Counterparty Limit Requests" placement="right">
                <Link
                  to="/counterparty-limit-requests"
                  className={`sn-item sn-item--collapsed${location.pathname === '/counterparty-limit-requests' ? ' is-active' : ''}`}
                >
                  <span className="sn-item-icon"><AttachMoney /></span>
                </Link>
              </Tooltip>
            </>
          ) : (
            <div className="sn-category">
              <div className="sn-menu">
                <NavLink to="/admin/dealer-limits" icon={<AttachMoney />} label="Dealer Limits" />
                <NavLink
                  to="/counterparty-limit-requests"
                  icon={<AttachMoney />}
                  label="Counterparty Limit Requests"
                />
              </div>
            </div>
          )
        )}
      </div>

      {collapsed && flyoutGroup && flyoutItems.length > 0 && createPortal(
        <div
          className="sn-flyout"
          style={{ left: DRAWER_WIDTH_COLLAPSED + 8, top: flyoutTop }}
          onMouseEnter={() => {
            if (flyoutCloseTimer.current) {
              clearTimeout(flyoutCloseTimer.current);
              flyoutCloseTimer.current = null;
            }
          }}
          onMouseLeave={scheduleCloseFlyout}
        >
          <div className="sn-flyout-title">{flyoutTitle}</div>
          <div className="sn-menu">
            {flyoutItems.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                icon={getIcon(tab.key)}
                label={tab.label}
                nested
                onNavigate={() => setFlyoutGroup(null)}
              />
            ))}
          </div>
        </div>,
        document.body
      )}

      <div className={`sn-footer${collapsed ? ' sn-footer--collapsed' : ''}`}>
        <NotificationBell collapsed={collapsed} />
        <Tooltip title={collapsed ? 'Change Password' : ''} placement="right" disableHoverListener={!collapsed}>
          <Link
            to="/change-password"
            className={`sn-footer-btn sn-footer-btn--password${collapsed ? ' sn-footer-btn--icon' : ''}`}
          >
            <VpnKey sx={{ fontSize: 18 }} />
            {!collapsed && 'Change Password'}
          </Link>
        </Tooltip>
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right" disableHoverListener={!collapsed}>
          <button
            type="button"
            onClick={handleLogout}
            className={`sn-footer-btn sn-footer-btn--logout${collapsed ? ' sn-footer-btn--icon' : ''}`}
          >
            <Logout sx={{ fontSize: 18 }} />
            {!collapsed && 'Logout'}
          </button>
        </Tooltip>
      </div>
    </Drawer>
  );
}

export default SideNav;
